<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">邮箱账户管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.keyword" placeholder="请输入账户名称或邮箱地址" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增账户
        </el-button>
      </div>

      <!-- 邮箱账户列表 -->
      <el-table :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="account_name" label="账户名称" width="150" />
        <el-table-column prop="email_service" label="邮箱服务" width="120" />
        <el-table-column prop="smtp_host" label="SMTP服务器" width="180" />
        <el-table-column prop="smtp_port" label="端口" width="80" />
        <el-table-column prop="email_address" label="邮箱地址" width="200" />
        <el-table-column prop="sender_name" label="发送名称" width="150" />
        <el-table-column prop="encryption" label="加密方式" width="100">
          <template #default="{ row }">
            <el-tag :type="row.encryption === 'ssl' ? 'success' : 'primary'">
              {{ row.encryption === 'ssl' ? 'SSL' : 'TLS' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="280" fixed="right">
          <template #default="scope">
            <el-button type="info" size="small" @click="openTestDialog(scope.row)">
              <el-icon>
                <Promotion />
              </el-icon>
              测试
            </el-button>
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchAccountList" @current-change="fetchAccountList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="700px" :close-on-click-modal="false">
      <el-form ref="accountForm" :model="accountForm" label-width="120px" :rules="formRules">
        <el-form-item label="账户名称" prop="account_name">
          <el-input v-model="accountForm.account_name" placeholder="请输入账户名称" size="large" />
        </el-form-item>

        <el-form-item label="邮箱服务">
          <el-select v-model="accountForm.email_service" placeholder="请选择邮箱服务" style="width: 100%" 
            @change="handleServiceChange" clearable>
            <el-option
              v-for="service in emailServices"
              :key="service.name"
              :label="service.name"
              :value="service.name"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="SMTP服务器" prop="smtp_host">
          <el-input v-model="accountForm.smtp_host" placeholder="请输入SMTP服务器地址" size="large" />
        </el-form-item>

        <el-form-item label="端口" prop="smtp_port">
          <el-input v-model.number="accountForm.smtp_port" placeholder="请输入端口号" size="large" type="number" />
        </el-form-item>

        <el-form-item label="加密方式">
          <el-radio-group v-model="accountForm.encryption">
            <el-radio label="tls">TLS</el-radio>
            <el-radio label="ssl">SSL</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="邮箱地址" prop="email_address">
          <el-input v-model="accountForm.email_address" placeholder="请输入邮箱地址" size="large" />
        </el-form-item>

        <el-form-item label="邮箱密码" prop="email_password">
          <el-input v-model="accountForm.email_password" placeholder="请输入邮箱密码或授权码" size="large" type="password" show-password />
        </el-form-item>

        <el-form-item label="发送名称" prop="sender_name">
          <el-input v-model="accountForm.sender_name" placeholder="请输入发送人显示名称" size="large" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="accountForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAccountForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 测试对话框 -->
    <el-dialog v-model="testDialogVisible" title="测试邮箱账户" width="500px" :close-on-click-modal="false">
      <el-form ref="testForm" :model="testForm" label-width="100px">
        <el-form-item label="收件人邮箱" prop="receiver_email">
          <el-input v-model="testForm.receiver_email" placeholder="请输入收件人邮箱地址" size="large" />
        </el-form-item>

        <el-form-item label="收件人名称">
          <el-input v-model="testForm.receiver_name" placeholder="请输入收件人名称（可选）" size="large" />
        </el-form-item>

        <el-form-item>
          <el-alert type="info" show-icon :closable="false">
            <template #title>
              <div>如果填写了收件人名称，测试邮件中将自动替换名称变量</div>
            </template>
          </el-alert>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="testDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTest" :loading="testing">发送测试邮件</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Promotion
} from '@element-plus/icons-vue'

export default {
  name: 'EmailAccount',
  components: { Search, Plus, Edit, Delete, Promotion },
  data() {
    return {
      loading: false,
      testing: false,
      dialogVisible: false,
      testDialogVisible: false,
      dialogTitle: '新增邮箱账户',
      isEditMode: false,
      currentAccountId: null,
      emailServices: [],
      searchForm: {
        keyword: ''
      },
      accountForm: {
        id: null,
        account_name: '',
        email_service: '',
        smtp_host: '',
        smtp_port: 587,
        email_address: '',
        email_password: '',
        sender_name: '',
        encryption: 'tls',
        status: 1,
        notes: ''
      },
      testForm: {
        receiver_email: '',
        receiver_name: ''
      },
      formRules: {
        account_name: [
          { required: true, message: '请输入账户名称', trigger: 'blur' }
        ],
        smtp_host: [
          { required: true, message: '请输入SMTP服务器地址', trigger: 'blur' }
        ],
        smtp_port: [
          { required: true, message: '请输入端口号', trigger: 'blur' },
          { type: 'number', message: '端口号必须为数字' }
        ],
        email_address: [
          { required: true, message: '请输入邮箱地址', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ],
        email_password: [
          { required: true, message: '请输入邮箱密码或授权码', trigger: 'blur' }
        ],
        sender_name: [
          { required: true, message: '请输入发送名称', trigger: 'blur' }
        ]
      },
      accountList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('pc_user_info') || '{}')
    }
  },
  mounted() {
    this.fetchAccountList();
    this.fetchEmailServices();
  },
  methods: {
    // 获取邮箱服务列表
    async fetchEmailServices() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/email_account.php`,
          {
            action: 'get_services',
            store_id: this.userInfo.store_id,
            user_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.emailServices = data.data;
        }
      } catch (error) {
        console.error('获取邮箱服务列表错误:', error);
      }
    },

    // 处理服务选择变化
    handleServiceChange(serviceName) {
      const service = this.emailServices.find(s => s.name === serviceName);
      if (service) {
        this.accountForm.smtp_host = service.smtp_host;
        this.accountForm.smtp_port = service.smtp_port;
        this.accountForm.encryption = service.encryption;
      }
    },

    // 获取账户列表
    async fetchAccountList() {
      this.loading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.store_id,
          user_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/pc/api/email_account.php`,
          params
        );

        if (data.data) {
          this.accountList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取邮箱账户列表失败');
        console.error('获取邮箱账户列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchAccountList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增邮箱账户';
      this.isEditMode = false;
      this.accountForm = {
        id: null,
        account_name: '',
        email_service: '',
        smtp_host: '',
        smtp_port: 587,
        email_address: '',
        email_password: '',
        sender_name: '',
        encryption: 'tls',
        status: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑邮箱账户';
      this.isEditMode = true;
      this.accountForm = {
        id: row.id,
        account_name: row.account_name,
        email_service: row.email_service,
        smtp_host: row.smtp_host,
        smtp_port: row.smtp_port,
        email_address: row.email_address,
        email_password: '', // 密码不显示，需要重新输入
        sender_name: row.sender_name,
        encryption: row.encryption,
        status: row.status,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 打开测试对话框
    openTestDialog(row) {
      this.currentAccountId = row.id;
      this.testForm = {
        receiver_email: '',
        receiver_name: ''
      };
      this.testDialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const { data } = await axios.post(
              `${config.BASE_URL}/pc/api/email_account.php`,
              {
                action,
                ...this.accountForm,
                store_id: this.userInfo.store_id,
                user_id: this.userInfo.id
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchAccountList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 提交测试
    submitTest() {
      this.$refs.testForm.validate(async (valid) => {
        if (valid) {
          this.testing = true;
          try {
            const { data } = await axios.post(
              `${config.BASE_URL}/pc/api/email_account.php`,
              {
                action: 'test',
                id: this.currentAccountId,
                store_id: this.userInfo.store_id,
                user_id: this.userInfo.id,
                ...this.testForm
              }
            );

            if (data.success) {
              this.$message.success('测试邮件发送成功');
              this.testDialogVisible = false;
            } else {
              this.$message.error(data.error || '测试失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '测试失败';
            this.$message.error(`测试失败: ${errorMsg}`);
            console.error('测试错误:', error);
          } finally {
            this.testing = false;
          }
        }
      });
    },

    // 删除账户
    async handleDelete(id) {
      this.$confirm('确定要删除该邮箱账户吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/pc/api/email_account.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.store_id,
              user_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchAccountList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}
</style>